import { useAppStore } from '../../store/useAppStore';
import { Project } from '../../types';
import { useState, useEffect } from 'react';

interface ProjectIndicatorProps {
  projects: Project[];
}

export const ProjectIndicator: React.FC<ProjectIndicatorProps> = ({
  projects,
}) => {
  const nearestProjectIndex = useAppStore(state => state.nearestProjectIndex);
  const isInitialAnimationComplete = useAppStore(
    state => state.isInitialAnimationComplete
  );
  const selectedIndex = useAppStore(state => state.selectedIndex);
  const setScrollOffset = useAppStore(state => state.setScrollOffset);
  const [showNearest, setShowNearest] = useState(false);

  useEffect(() => {
    if (isInitialAnimationComplete) {
      setShowNearest(true);
    }
  }, [isInitialAnimationComplete]);

  if (selectedIndex !== null) return null;

  // Réorganiser les points pour que le milieu soit le premier
  const reorderedIndexes = projects.map((_, i) => {
    const length = projects.length;
    const middle = Math.floor(length / 2);
    let newIndex;

    if (i <= middle) {
      // Première moitié : commencer par le milieu
      newIndex = middle - i;
    } else {
      // Deuxième moitié : continuer après le milieu
      newIndex = length - (i - middle);
    }

    return newIndex;
  });

  const handleClick = (originalIndex: number) => {
    return;
    const anglePerProject = (Math.PI * 2) / projects.length;
    const currentOffset = useAppStore.getState().scrollOffset;
    const currentIndex = Math.round((currentOffset * 2) / anglePerProject);

    let diff = originalIndex - (currentIndex % projects.length);

    if (diff > projects.length / 2) {
      diff -= projects.length;
    } else if (diff < -projects.length / 2) {
      diff += projects.length;
    }

    const targetOffset = currentOffset + (diff * anglePerProject) / 2;

    // Animation fluide
    const startOffset = currentOffset;
    const distance = targetOffset - startOffset;
    const duration = 1000; // durée en ms
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Fonction d'easing (easeOutCubic)
      const eased = 1 - Math.pow(1 - progress, 3);

      const currentPosition = startOffset + distance * eased;
      setScrollOffset(currentPosition);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
    useAppStore.getState().setHasScrolled(true);
  };

  return (
    <div className="fixed left-8 top-1/2 -translate-y-1/2 flex flex-col-reverse gap-3">
      {reorderedIndexes.map((originalIndex, i) => (
        <div
          key={originalIndex}
          onClick={() => handleClick(originalIndex)}
          className="w-4 flex items-center justify-center"
        >
          <div
            className={`
              rounded-full 
              transition-all 
              duration-700
              ease-[cubic-bezier(0.34,1.56,0.64,1)]
              opacity-0
              ${
                isInitialAnimationComplete
                  ? showNearest &&
                    originalIndex === reorderedIndexes[nearestProjectIndex]
                    ? 'w-4 h-4 bg-black scale-[1.4]'
                    : 'w-3 h-3 bg-black/40 hover:scale-125'
                  : 'w-3 h-3 bg-black/40'
              }
            `}
            style={{
              animation: 'fade-in 0.5s ease-out forwards',
              animationDelay: `${1000 + (reorderedIndexes.length - 1 - i) * 120}ms`,
            }}
          />
        </div>
      ))}
    </div>
  );
};
